import * as React from 'react';

import { getLocale } from '@rematter/common/locale';
import { enumUnit } from '@rematter/frontend-lib-api-client';
import { convertWeightForPricing } from '@rematter/modules/trade/utils/weight';
import {
  Button,
  cn,
  formatPercent,
  humanReadableWeight,
  weightToPounds,
} from '@rematter/ui-web';
import Big from 'big.js';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePlus } from 'lucide-react';

import {
  $addLineItem,
  $deleteEntry,
  $itemsInterfaceDisabled,
  $itemsInterfaceFacilityDefaultUnit,
  $itemsInterfaceInventoryLocked,
  $itemsInterfacePricingLocked,
  $itemsInterfaceWeightUnitMode,
  $updateLineItem,
} from '../_state';
import { NormalizedLineItem } from '../_types';
import {
  DeleteButtonField,
  EditItemButtonField,
  EstimatedPriceField,
  GrossWeightField,
  InventoryItemField,
  NetWeightField,
  TareWeightField,
} from '../fields';
import {
  GRID_COLS_PRICING,
  ITEM_ROW_ERROR_BORDER,
  ITEM_ROW_FOCUS_WITHIN,
  ItemRow,
} from '../layout';

import { ItemRowMixedChild } from './ItemRowMixedChild';

import { $inventoryItemsById } from '~/modules/trade/_state/loadForm.state';
import {
  $openMixedChildAdd,
  $openMixedDetails,
} from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { useUIPreferences } from '~/shared/hooks';

type ItemRowMixedProps = {
  lineItem: NormalizedLineItem;
  childItems: NormalizedLineItem[];
  autoFocus?: boolean;
  newlyAddedItemId?: string | null;
  onFocusRow?: (id: string) => void;
  hasError?: boolean;
  errorItemIds?: Set<string>;
};

export const ItemRowMixed: React.FC<ItemRowMixedProps> = ({
  lineItem,
  childItems,
  autoFocus = false,
  newlyAddedItemId,
  onFocusRow,
  hasError,
  errorItemIds,
}) => {
  const t = useTranslations();

  const disabled = useAtomValue($itemsInterfaceDisabled);
  const inventoryLocked = useAtomValue($itemsInterfaceInventoryLocked);
  const pricingLocked = useAtomValue($itemsInterfacePricingLocked);

  const weightUnit = useAtomValue($itemsInterfaceWeightUnitMode);
  const facilityDefaultUnit = useAtomValue($itemsInterfaceFacilityDefaultUnit);
  const onUpdateItem = useSetAtom($updateLineItem);
  const onDeleteItem = useSetAtom($deleteEntry);
  const onAddItem = useSetAtom($addLineItem);

  const grossWeightRef = React.useRef<HTMLDivElement>(null);
  const inventorySelectRef = React.useRef<HTMLDivElement>(null);

  const openMixedChildAdd = useSetAtom($openMixedChildAdd);
  const openMixedDetails = useSetAtom($openMixedDetails);
  const inventoryItemsById = useAtomValue($inventoryItemsById);
  const { touchModeEnabled } = useUIPreferences();

  const parentNetWeight = lineItem.weights?.[enumLoadWeightType.Net];
  const parentGrossWeight = lineItem.weights?.[enumLoadWeightType.Gross];

  const totalChildWeight = React.useMemo(
    () =>
      childItems
        .reduce((sum, child) => {
          const childNet = new Big(
            child.weights?.[enumLoadWeightType.Net]?.value ?? 0,
          ).toNumber();

          return sum.plus(childNet);
        }, new Big(0))
        .toNumber(),
    [childItems],
  );

  const totalPercentage = React.useMemo(() => {
    if (
      childItems.length === 0 ||
      !parentNetWeight?.value ||
      new Big(parentNetWeight.value).eq(0)
    )
      return 0;

    return new Big(totalChildWeight)
      .div(new Big(parentNetWeight?.value ?? 0))
      .times(100)
      .toNumber();
  }, [parentNetWeight?.value, childItems]);

  const totalChildEstimatedPrice = React.useMemo(
    () =>
      childItems
        .reduce((sum, child) => {
          const netWeight = child.weights?.[enumLoadWeightType.Net] ?? null;
          const convertedWeightPounds = weightToPounds(netWeight);
          const convertedNetWeight = convertWeightForPricing(
            convertedWeightPounds,
            child.pricingUnit ?? enumUnit.Pounds,
          );

          return sum.plus(
            new Big(child.unitPrice?.amount ?? 0).times(convertedNetWeight),
          );
        }, new Big(0))
        .toNumber(),
    [childItems],
  );

  const canAddChild = (parentGrossWeight?.value ?? 0) > 0;
  const inventoryFieldsDisabled = disabled || inventoryLocked;
  const pricingFieldsDisabled = disabled || pricingLocked;

  const baseFieldProps = {
    lineItem,
    onUpdateItem,
    onDeleteItem,
    weightUnit,
    facilityDefaultUnit,
  };

  const inventoryFieldProps = {
    ...baseFieldProps,
    disabled: inventoryFieldsDisabled,
  };

  const pricingFieldProps = {
    ...baseFieldProps,
    disabled: pricingFieldsDisabled,
  };

  const percentageColor =
    Math.round(totalPercentage) === 100 ? 'text-success' : 'text-failure';

  const handleEditMixed = React.useCallback(() => {
    const parentNet = parentNetWeight?.value ?? 0;
    const breakdown = childItems
      .map((child) => {
        const childNet = child.weights?.[enumLoadWeightType.Net]?.value ?? 0;
        const percentage =
          parentNet > 0
            ? new Big(childNet).div(parentNet).times(100).toNumber()
            : 0;
        const inventoryItem = child.itemId
          ? inventoryItemsById[child.itemId]
          : undefined;
        return {
          itemId: child.itemId ?? '',
          itemName: inventoryItem?.name ?? '',
          itemGroup: inventoryItem?.itemGroup?.name,
          mnemonic: inventoryItem?.mnemonic,
          percentage: Math.round(percentage * 100) / 100,
        };
      })
      .filter((b) => b.itemId !== '');

    openMixedDetails({
      lineItemId: lineItem.id,
      weights: lineItem.weights ?? {},
      notes: lineItem.notes ?? '',
      breakdown,
    });
  }, [
    openMixedDetails,
    lineItem,
    childItems,
    inventoryItemsById,
    parentNetWeight?.value,
  ]);

  const handleAddChildItem = React.useCallback(() => {
    onAddItem('Raw', {
      parentLineItemId: lineItem.id,
      pricingUnit: lineItem.pricingUnit ?? enumUnit.Pounds,
    });
  }, [onAddItem, lineItem.id, lineItem.pricingUnit]);

  const handleTouchAddChildItem = React.useCallback(() => {
    openMixedChildAdd(lineItem.id);
  }, [openMixedChildAdd, lineItem.id]);

  React.useEffect(() => {
    if (!autoFocus) return;

    const targetRef = touchModeEnabled ? grossWeightRef : inventorySelectRef;
    const inputElement = targetRef?.current?.querySelector('input');
    if (inputElement) {
      setTimeout(() => {
        inputElement.focus();
        inputElement.select();
      }, 100);
    }
  }, [autoFocus, touchModeEnabled]);

  return (
    <>
      <ItemRow
        gridTemplate={GRID_COLS_PRICING}
        className={cn(
          '@ItemRowMixed relative',
          ITEM_ROW_FOCUS_WITHIN,
          hasError && ITEM_ROW_ERROR_BORDER,
        )}
        onFocus={() => onFocusRow?.(lineItem.id)}
      >
        <div ref={inventorySelectRef}>
          <InventoryItemField
            {...inventoryFieldProps}
            lineItem={lineItem}
            symbolDescription={
              childItems.length > 0 ? childItems.length.toString() : undefined
            }
          />
        </div>

        <div ref={grossWeightRef}>
          <GrossWeightField {...inventoryFieldProps} />
        </div>

        <TareWeightField {...inventoryFieldProps} />
        <NetWeightField {...inventoryFieldProps} />
        <div />
        <div />
        <EstimatedPriceField
          {...pricingFieldProps}
          overrideValue={totalChildEstimatedPrice}
        />

        <EditItemButtonField
          {...inventoryFieldProps}
          onClick={handleEditMixed}
        />
        <div />
        <DeleteButtonField {...inventoryFieldProps} />
      </ItemRow>

      {childItems.map((childItem) => (
        <ItemRowMixedChild
          key={childItem.id}
          lineItem={childItem}
          parentLineItem={lineItem}
          autoFocus={newlyAddedItemId === childItem.id}
          hasError={errorItemIds?.has(childItem.id)}
        />
      ))}

      {!disabled && !inventoryLocked && (
        <div
          className={cn(
            'relative grid items-center gap-3 py-2',
            'after:absolute after:right-0 after:bottom-0 after:left-[0] after:h-px after:bg-neutral/20',
            GRID_COLS_PRICING,
          )}
        >
          <div className="flex items-center pl-[40px]">
            <Button
              variant="text"
              className="text-xs"
              isDisabled={!canAddChild}
              onClick={
                touchModeEnabled ? handleTouchAddChildItem : handleAddChildItem
              }
            >
              <LucidePlus size={14} />
              <span>{t.scaleTicketPro.actions.addMixedMaterial}</span>
            </Button>
          </div>
          <div />
          <div
            className={cn(
              'flex items-center justify-end text-xs font-medium',
              childItems.length > 0 ? percentageColor : 'text-neutral/60',
            )}
          >
            {childItems.length > 0 &&
              formatPercent(Math.round(totalPercentage))}
          </div>
          <div
            className={cn(
              'flex items-center justify-end text-xs font-medium',
              childItems.length > 0 ? percentageColor : 'text-neutral/60',
            )}
          >
            {childItems.length > 0 &&
              humanReadableWeight(Math.round((totalChildWeight * 100) / 100), {
                locale: getLocale(),
                decimalPlaces: 2,
              })}
          </div>
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
        </div>
      )}
    </>
  );
};
